// The pictures of the MODDING options (R1909, R1918, R2084,
// ui-map-interface.md §16.3, card 01 of mockup round 092).
//
// A picture of an option is a REAL frame of the theme preview (R806): the
// baker builds a second `ThemePreview` in a host the reader does not see,
// shows one option in it, lets the loop run until the loop of the focus
// shows its thing, and copies that frame. The copy is the copy of photo
// mode (`fast/capture.ts`): it runs in the task of the next frame, right
// after the submit, so it reads the picture on both backends. No picture
// is drawn by hand, so a picture never differs from the game.
//
// **The frame** (R1918). The baker's preview shows the map alone
// (`mapOnly`), nearer than the focus's own frame: a picture of one mark
// divides the spans of its focus by `[presentation.menu] picture_zoom`, and
// a picture whose thing reaches wide by `picture_wide_zoom` (`zoomOf`).
// The camera snaps onto each focus (`snap`), so a job waits for its content
// alone: the look of the job draws on the first frame after the show, with
// the first state frame of the staged fleets and the mark it brings, and
// the clock of the job starts there. A round waits until it is well on its
// way. The copy keeps the pixels of the canvas, which the engine draws at
// the device pixel ratio (at most 2).
//
// **What it costs.** A picture is baked once. The page keeps it in a map
// keyed by the look it shows (`pictureKey`), and the browser keeps it in
// the picture store (`mods-picstore.ts`) over a reload. A key the store
// holds from this build shows with no engine at all. A key the store holds
// from another build is an OLD picture (R2084): it shows at once, the
// baker bakes it again after every miss of the open page, and the new
// picture replaces it in its box. The baker builds its preview only for a
// miss and an old picture. It bakes one picture at a time, at
// `[presentation.menu] picture_clock` times the menu rate with a clock as
// many times as fast (`ThemePreview` `fast`, R2084): each frame steps the
// clock as a menu frame does, so the picture is what the menu preview
// shows, in less wall time. The encode of a copy runs while the next job
// plays. The pictures of the open page go first: after each rewrite the panel hands the keys of its
// boxes to `only`, which puts them at the front in the page order and
// drops a waiting key that no page shows any more. The baker destroys its
// preview, with its context, `IDLE_MS` after the queue runs dry. The 4 Hz
// redraw of the hub reads the cache and never bakes. A picture is a still,
// so it costs no frame after it is baked, and reduced motion changes
// nothing.
//
// **The host.** The preview's loop stops while its frame is off screen
// (R806), so the host stands in the viewport at full size with opacity 0
// and no pointer events. It stands under `document.body`, outside the
// chrome zoom of the UI scale (R1556), so a picture has one size for every
// step. Its frame is bare and takes no chrome id (R1914), so the baker may
// start before the panel's own preview: the count of the board holders of
// `preview.ts` puts the page's grid back whichever preview goes last.
//
// The picture is the canvas alone: the chart cards of words and the chrome
// of the four DOM foci are not in it, as photo mode keeps no DOM.

import { createEngine as realEngine, type Engine } from '../../../fast/engine';
import type { FrameInfo } from '../../../fast/layer';
import { MENU_PICTURE_CLOCK, MENU_PICTURE_KEEP, MENU_PICTURE_WIDE_ZOOM, MENU_PICTURE_ZOOM } from '../../../gen/presentation';
import { ThemePreview, type PreviewEngineFactory, type PreviewFocus, type PreviewLook } from '../../../preview';
import { MAX_DPR, deviceRatio } from '../../../render/dpr';
import { browserStore, type PictureStore, type StoredPicture } from './mods-picstore';
import { fnv1a } from '../../../render/fnv';

/** One picture: what the preview shows for it. */
export interface PictureJob {
  focus: PreviewFocus;
  look: PreviewLook;
}

/** The size of the host, in CSS px: the 16 / 10 frame of the PREVIEW
 *  column (`hub.css` `.mframe`), so a picture frames what the column
 *  frames. The copy keeps the pixels the engine draws in it. */
const PICTURE_W = 320;
const PICTURE_H = 200;

/** How long a job runs before the copy, in seconds of the preview's clock,
 *  counted from the first frame that draws its look. The camera snaps, so
 *  a job waits one state frame of 10 Hz and the mark it brings. The first
 *  job of a new engine waits longer, for its first uploads and pipelines.
 *  A round flies from the start of its loop, so a torpedo picture waits
 *  until the round is well on its way. */
export const LOOK_SEC = 0.1;
export const FIRST_SEC = 0.5;
export const ROUND_SEC = 1.0;
const ROUND_FOCI: ReadonlySet<PreviewFocus> = new Set<PreviewFocus>(['torpedo', 'effectTorpedo']);

/** The foci whose thing reaches wide: the chart pieces and the terrain of
 *  THE WHOLE LOOK, the two rows of the map markers, an effect on a hull or
 *  a hold, whose marks reach 46 and 62 px off the object (`effects.toml`),
 *  the current field and its heat look, which cover the open water (R2716,
 *  R2869), and the depth
 *  bands, whose edges lie hexes apart (R2726). */
const WIDE_FOCI: ReadonlySet<PreviewFocus> = new Set<PreviewFocus>([
  'chart', 'mapMarker', 'effectFleet', 'effectHold', 'currents', 'currentsHeat', 'depth',
]);

/** The zoom of a picture over its focus's own frame (R1918). */
export function pictureZoom(focus: PreviewFocus): number {
  return WIDE_FOCI.has(focus) ? MENU_PICTURE_WIDE_ZOOM : MENU_PICTURE_ZOOM;
}

/** How long the baker keeps its preview after the queue runs dry, in ms. */
const IDLE_MS = 2000;

/** The baked pictures, by key, for the life of the page. A rebake of an
 *  old picture replaces its address and does not revoke the old one: the
 *  newest picture of an option (`mods-looks.ts`) may still show it in a
 *  box of another key, and a revoke would blank that box. An old picture
 *  is baked again once a page life, so the cost is one page of blobs. */
const baked = new Map<string, string>();
/** The keys whose copy gave no image. They are not asked again. */
const refused = new Set<string>();
/** The keys whose picture in `baked` is an old picture of another build:
 *  shown, and waiting for a bake of this build (R2084). A key stays here
 *  until that bake ends, so a later page that shows it asks again. */
const old = new Set<string>();

/** FNV-1a over a string, as 8 hex digits. */
export function hashText(text: string): string {
  return fnv1a(text).toString(16).padStart(8, '0');
}

/** The key of one picture: the look it shows, by its parts. */
export function pictureKey(...parts: string[]): string {
  return `p${hashText(parts.join('|'))}`;
}

/** The picture of one key, or undefined while it is not baked. */
export function pictureUrl(key: string): string | undefined {
  return baked.get(key);
}

/** What the panel needs of a baker. A test hands over a recorder. */
export interface Bakery {
  /** Asks for one picture. `make` builds the job, and runs only where the
   *  picture is neither baked nor waiting. */
  want(key: string, make: () => PictureJob): void;
  /** The keys the open page shows, in the page order: they go to the
   *  front of the queue, and a waiting key not among them is dropped. */
  only(keys: readonly string[]): void;
  /** Starts the queue. */
  start(): void;
  /** Calls `fn` with each key the baker finishes. */
  onBaked(fn: (key: string, url: string) => void): void;
  /** Stops for good: the queue, the preview and its context. The cache stays. */
  destroy(): void;
}

/** The preview the baker drives: `ThemePreview`, or a test's recorder. */
export interface BakePreview {
  readonly ready: Promise<boolean>;
  show(focus: PreviewFocus, look: PreviewLook): void;
  destroy(): void;
}

export interface BakerDeps {
  /** Builds the preview in `host`, with its engine from `engine`. */
  makePreview?: (host: HTMLElement, engine: PreviewEngineFactory) => BakePreview;
  /** The engine factory the wrapper calls. */
  createEngine?: PreviewEngineFactory;
  /** Encodes a copy, or null where the browser gives none. */
  toBlob?: (canvas: HTMLCanvasElement) => Promise<Blob | null>;
  /** Turns a picture into an image address. */
  toUrl?: (blob: Blob) => string;
  /** Whether the browser can draw a preview at all. */
  canDraw?: () => boolean;
  /** The picture store over a reload; null keeps none. */
  store?: PictureStore | null;
  /** The pixels of a CSS px in the copy: the device pixel ratio, 1 or 2. */
  scale?: () => number;
}

/** How many times the menu rate and clock the baker's preview runs at. */
export const BAKE_FAST = MENU_PICTURE_CLOCK;

/** The encoded copy. WebP keeps a picture small in the store; a browser
 *  that cannot encode it gives PNG, which the spec names as the fallback.
 *  The page policy allows `img-src blob:` (R147). */
function encode(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => {
    try {
      canvas.toBlob((b) => resolve(b), 'image/webp', 0.9);
    } catch {
      resolve(null);
    }
  });
}

function objectUrl(blob: Blob): string {
  try {
    return URL.createObjectURL(blob);
  } catch {
    return '';
  }
}

/** A browser with neither WebGPU nor WebGL2 draws no preview, so the baker
 *  builds none and the tiles keep their names. */
function browserDraws(): boolean {
  return typeof WebGL2RenderingContext !== 'undefined'
    || (typeof navigator !== 'undefined' && 'gpu' in navigator);
}

/** The device pixel ratio as the engine's canvas takes it: 1 to `MAX_DPR`. */
function deviceScale(): number {
  return Math.max(1, Math.round(deviceRatio(MAX_DPR)));
}

/** The build of this client, or '' where no build stamped it. */
const BUILD = typeof __AP_BUILD__ === 'string' ? __AP_BUILD__ : '';

/** One waiting picture: the job, whether the store answered a miss, and
 *  whether the picture shown now is an old one, which waits behind every
 *  miss (R2084). */
interface Waiting {
  job: PictureJob;
  missed: boolean;
  old: boolean;
}

export class PictureBaker implements Bakery {
  private readonly queue = new Map<string, Waiting>();
  private readonly listeners: ((key: string, url: string) => void)[] = [];
  private readonly store: PictureStore | null;
  private host: HTMLDivElement | null = null;
  private preview: BakePreview | null = null;
  private engine: Engine | null = null;
  private job: { key: string; need: number; drawn: boolean } | null = null;
  /** The next job is the first of a new engine. */
  private first = true;
  private elapsed = 0;
  private copying = false;
  private idle: ReturnType<typeof setTimeout> | null = null;
  private failed = false;
  private dead = false;
  private started = false;

  constructor(private readonly deps: BakerDeps = {}) {
    this.store = deps.store !== undefined ? deps.store : browserStore(BUILD, MENU_PICTURE_KEEP);
  }

  want(key: string, make: () => PictureJob): void {
    if (this.dead || refused.has(key) || this.queue.has(key)) return;
    if (baked.has(key) && !old.has(key)) return;
    if (this.job?.key === key) return;
    if (old.has(key)) {
      // An old picture shows already: it waits for a bake alone.
      if (this.failed) return;
      this.queue.set(key, { job: make(), missed: true, old: true });
      if (this.started) this.pump();
      return;
    }
    // A browser that cannot draw still reads the store.
    if (this.failed && this.store === null) return;
    this.queue.set(key, { job: make(), missed: this.store === null, old: false });
    if (this.store !== null) void this.lookUp(key);
    else if (this.started) this.pump();
  }

  only(keys: readonly string[]): void {
    const front = new Map<string, Waiting>();
    for (const k of keys) {
      const w = this.queue.get(k);
      if (w !== undefined) front.set(k, w);
    }
    this.queue.clear();
    for (const [k, w] of front) this.queue.set(k, w);
  }

  start(): void {
    this.started = true;
    this.pump();
  }

  onBaked(fn: (key: string, url: string) => void): void {
    this.listeners.push(fn);
  }

  destroy(): void {
    this.dead = true;
    this.queue.clear();
    this.takeDown();
  }

  /** Whether a preview stands now: for a test of its life. */
  get building(): boolean { return this.preview !== null; }

  // -- the store ------------------------------------------------------------------

  /** The key of a picture in the store: the look and the pixel scale. */
  private stored(key: string): string {
    return `${key}@${(this.deps.scale ?? deviceScale)()}`;
  }

  /** Asks the store for one waiting key. A hit of this build shows with no
   *  engine; a miss lets the queue bake it; an old hit shows at once and
   *  waits for a bake behind every miss (R2084). */
  private async lookUp(key: string): Promise<void> {
    let hit: StoredPicture | null = null;
    try {
      hit = await this.store!.get(this.stored(key));
    } catch {
      hit = null;
    }
    if (this.dead) return;
    const w = this.queue.get(key);
    const url = hit === null || baked.has(key) ? '' : (this.deps.toUrl ?? objectUrl)(hit.blob);
    if (baked.has(key)) {
      this.queue.delete(key);
    } else if (url !== '') {
      // A dropped key that the store holds is shown all the same: it costs
      // no engine. An old one stays in the queue for its bake, unless the
      // page dropped it or the browser draws no preview; `old` keeps it
      // for the next page that asks.
      if (hit!.current) {
        this.queue.delete(key);
      } else {
        old.add(key);
        if (w !== undefined && !this.failed) {
          w.missed = true;
          w.old = true;
        } else {
          this.queue.delete(key);
        }
      }
      this.finish(key, url);
    } else if (w !== undefined) {
      // A miss: the queue bakes it, unless the browser draws no preview.
      if (this.failed) this.queue.delete(key);
      else w.missed = true;
    }
    this.pump();
  }

  private finish(key: string, url: string): void {
    baked.set(key, url);
    for (const fn of this.listeners) fn(key, url);
  }

  // -- the queue ----------------------------------------------------------------

  private pump(): void {
    if (this.dead || this.failed || !this.started || this.job !== null) return;
    // Every miss goes before an old picture, which shows already (R2084).
    // An old picture also waits while a key still waits for the store's
    // answer, which may be a miss; that answer pumps again.
    let next: [string, Waiting] | undefined;
    let asking = false;
    for (const e of this.queue) {
      if (!e[1].missed) asking = true;
      else if (!e[1].old) { next = e; break; }
    }
    if (next === undefined && asking) return;
    if (next === undefined) for (const e of this.queue) if (e[1].missed) { next = e; break; }
    if (next === undefined) {
      this.idleLater();
      return;
    }
    if (this.idle !== null) { clearTimeout(this.idle); this.idle = null; }
    if (this.preview === null && !this.build()) return;
    const [key, { job }] = next;
    this.queue.delete(key);
    const need = ROUND_FOCI.has(job.focus) ? ROUND_SEC : this.first ? FIRST_SEC : LOOK_SEC;
    this.first = false;
    this.job = { key, need, drawn: false };
    this.elapsed = 0;
    this.copying = false;
    this.preview!.show(job.focus, job.look);
  }

  /** Takes the preview down `IDLE_MS` after the last job, while no job waits. */
  private idleLater(): void {
    if (this.preview === null || this.idle !== null) return;
    this.idle = setTimeout(() => { this.idle = null; this.takeDown(); }, IDLE_MS);
  }

  private build(): boolean {
    if (!(this.deps.canDraw ?? browserDraws)()) {
      this.fail();
      return false;
    }
    const host = document.createElement('div');
    host.setAttribute('aria-hidden', 'true');
    host.style.cssText = `position:fixed;left:0;top:0;width:${PICTURE_W}px;height:${PICTURE_H}px;`
      + 'opacity:0;pointer-events:none;z-index:-1;overflow:hidden;';
    document.body.append(host);
    this.host = host;
    const make = this.deps.createEngine ?? realEngine;
    const engine: PreviewEngineFactory = (canvas, opts) => make(canvas, {
      ...opts,
      onFrame: (f) => { opts.onFrame?.(f); this.frame(f); },
    }).then((e) => { this.engine = e; return e; });
    const build = this.deps.makePreview
      ?? ((h, e) => new ThemePreview(h, { createEngine: e, mapOnly: true, zoom: pictureZoom, snap: true, fast: BAKE_FAST }));
    const preview = build(host, engine);
    this.preview = preview;
    this.first = true;
    void preview.ready.then((ok) => { if (!ok && this.preview === preview) this.fail(); });
    return true;
  }

  /** The browser gave no context: the tiles keep their names for good,
   *  except the pictures the store holds. */
  private fail(): void {
    this.failed = true;
    for (const [k, w] of this.queue) if (w.missed) this.queue.delete(k);
    this.takeDown();
  }

  private takeDown(): void {
    if (this.idle !== null) { clearTimeout(this.idle); this.idle = null; }
    this.preview?.destroy();
    this.preview = null;
    this.engine = null;
    this.host?.remove();
    this.host = null;
    this.job = null;
  }

  // -- the frame ------------------------------------------------------------------

  /** Runs after the preview's own frame work. The clock of the job starts
   *  at the first frame that draws its look; the copy waits for the clock
   *  of the job, then takes the next frame. */
  private frame(f: FrameInfo): void {
    const job = this.job, engine = this.engine;
    if (job === null || engine === null || this.copying) return;
    if (!job.drawn) {
      job.drawn = true;
      return;
    }
    this.elapsed += f.dtMs / 1000;
    if (this.elapsed < job.need) return;
    this.copying = true;
    engine.loop.afterNextFrame(() => { void this.copy(engine, job.key); });
  }

  /** Copies the frame into a canvas of its own, gives the engine to the
   *  next job at once, and encodes the copy while that job plays. */
  private async copy(engine: Engine, key: string): Promise<void> {
    if (this.dead || this.job?.key !== key) return;
    const src = engine.canvas;
    let out: HTMLCanvasElement | null = null;
    if (src.width > 0 && src.height > 0) {
      const c = document.createElement('canvas');
      c.width = src.width;
      c.height = src.height;
      const g = c.getContext('2d');
      if (g) {
        g.drawImage(src, 0, 0, src.width, src.height);
        out = c;
      }
    }
    this.job = null;
    this.pump();
    const blob = out === null ? null : await (this.deps.toBlob ?? encode)(out);
    if (this.dead) return;
    const url = blob === null ? '' : (this.deps.toUrl ?? objectUrl)(blob);
    // A failed copy of an old picture keeps the old picture in its box.
    old.delete(key);
    if (url === '') refused.add(key);
    else {
      this.finish(key, url);
      if (this.store !== null && blob !== null) void this.store.put(this.stored(key), blob).catch(() => undefined);
    }
  }
}

/** Drops every baked picture: for a test. */
export function resetPictures(): void {
  baked.clear();
  refused.clear();
  old.clear();
}
